import { signInWithGoogle, signUpWithEmail } from '../lib/supabase'

const EMAIL_STORAGE_KEY = 'lawboost:userEmail'
const CLIENT_WEBSITE_STORAGE_KEY = 'lawboost:clientWebsite'

const form = document.getElementById('signup-form') as HTMLFormElement | null
const emailInput = document.getElementById('email') as HTMLInputElement | null
const passwordInput = document.getElementById('password') as HTMLInputElement | null
const submitBtn = document.getElementById('submit-btn') as HTMLButtonElement | null
const btnText = document.getElementById('btn-text') as HTMLElement | null
const btnLoader = document.getElementById('btn-loader') as HTMLElement | null
const errorMessage = document.getElementById('error-message') as HTMLElement | null
const successMessage = document.getElementById('success-message') as HTMLElement | null
const googleBtn = document.getElementById('google-signin-btn') as HTMLButtonElement | null
const searchParams = new URLSearchParams(window.location.search)
const emailFromQuery = searchParams.get('email')?.trim() || null
const clientWebsiteFromQuery = searchParams.get('url')?.trim() || null

if (
  !form ||
  !emailInput ||
  !passwordInput ||
  !submitBtn ||
  !btnText ||
  !btnLoader ||
  !errorMessage ||
  !successMessage ||
  !googleBtn
) {
  console.error('Signup form failed to initialize because required elements are missing.')
} else {
  const showError = (message: string) => {
    errorMessage.textContent = message
    errorMessage.style.display = 'block'
    successMessage.style.display = 'none'
  }

  const showSuccess = (message: string) => {
    successMessage.textContent = message
    successMessage.style.display = 'block'
    errorMessage.style.display = 'none'
  }

  const hideMessages = () => {
    errorMessage.style.display = 'none'
    successMessage.style.display = 'none'
  }

  const rememberEmail = (value: string) => {
    try {
      localStorage.setItem(EMAIL_STORAGE_KEY, value)
    } catch (storageError) {
      console.warn('Unable to store signup email locally.', storageError)
    }
  }

  const rememberClientWebsite = (value: string | null) => {
    if (!value) return
    try {
      localStorage.setItem(CLIENT_WEBSITE_STORAGE_KEY, value)
    } catch (storageError) {
      console.warn('Unable to store client website locally.', storageError)
    }
  }

  const prefillEmail = () => {
    let initialEmail = emailFromQuery || null

    if (!initialEmail) {
      try {
        initialEmail = localStorage.getItem(EMAIL_STORAGE_KEY) || null
      } catch (storageError) {
        console.warn('Unable to read stored signup email.', storageError)
      }
    }

    if (initialEmail && emailInput) {
      emailInput.value = initialEmail
      rememberEmail(initialEmail)
    }
  }

  const setLoading = (isLoading: boolean) => {
    submitBtn.disabled = isLoading
    googleBtn.disabled = isLoading
    btnText.style.display = isLoading ? 'none' : 'inline'
    btnLoader.style.display = isLoading ? 'inline' : 'none'
  }

  prefillEmail()

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    hideMessages()

    const email = emailInput.value.trim()
    const password = passwordInput.value

    if (!email || !email.includes('@')) {
      showError('Please enter a valid email address')
      return
    }

    if (!password || password.length < 6) {
      showError('Password must be at least 6 characters')
      return
    }

    rememberEmail(email)
    rememberClientWebsite(clientWebsiteFromQuery)
    setLoading(true)

    try {
      const { data, error } = await signUpWithEmail(email, password, clientWebsiteFromQuery)

      if (error) {
        if (error.message && error.message.includes('already registered')) {
          showError('This email is already registered. Please log in instead.')
        } else {
          showError(error.message || 'An error occurred during signup')
        }
        return
      }

      if (data && data.user) {
        showSuccess('Account created successfully! Redirecting...')
        form.reset()

        if (typeof fbq !== 'undefined') {
          fbq('track', 'CompleteRegistration')
        }

        setTimeout(() => {
          window.location.href = '/pricing'
        }, 500)
      }
    } catch (err) {
      console.error('Signup error:', err)
      showError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  })

  googleBtn.addEventListener('click', async () => {
    hideMessages()
    setLoading(true)
    rememberClientWebsite(clientWebsiteFromQuery)

    try {
      const { error } = await signInWithGoogle()

      if (error) {
        showError(error.message || 'Failed to sign in with Google')
      } else {
        if (typeof fbq !== 'undefined') {
          fbq('track', 'CompleteRegistration')
        }
        showSuccess('Redirecting to Google...')
      }
    } catch (err) {
      console.error('Google signin error:', err)
      showError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  })

  emailInput.addEventListener('input', hideMessages)
  passwordInput.addEventListener('input', hideMessages)
}
